import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Typography, Box, FormGroup, createTheme, ThemeProvider } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { SwitchWithCustomText, ISwitchWithCustomText } from '../Switch/SwitchWithCustomText';
import { IUserConfiguration } from '../../redux/configuration/types';

export interface ISettingsProps extends Omit<ISwitchWithCustomText, 'header' | 'body' | 'theme'> {
    onChange: (payload: any) => void;
    setting: IUserConfiguration;
}

const AccountPreference: React.FC<ISettingsProps> = props => {
    const { onChange, setting } = props;
    const [settingValue, setSettingsValue] = useState<any>();

    useEffect(() => {
        if (setting) {
            setSettingsValue({
                personalized_controls: setting.personalized_controls || {
                    hls_play: 'inactive',
                    contest_emails: 'inactive',
                    ads: 'inactive',
                    audition: 'inactive',
                    movie_ticket: 'inactive',
                    voting: 'inactive',
                    acts: 'inactive',
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);

    const switchTheme = createTheme({
        components: {
            MuiSwitch: {
                styleOverrides: {
                    switchBase: {
                        // Controls default (unchecked) color for the thumb
                        color: theme[themeStyle].tag_white,
                    },
                    colorPrimary: {
                        '&.Mui-checked': {
                            // Controls checked color for the thumb
                            color: theme[themeStyle].tag_white,
                        },
                    },
                    track: {
                        // Controls default (unchecked) color for the track
                        opacity: 1,
                        backgroundColor: theme[themeStyle].gray_message,
                        '.Mui-checked.Mui-checked + &': {
                            // Controls checked color for the track
                            opacity: 0.9,
                            backgroundColor: theme[themeStyle].primary_orange,
                        },
                    },
                },
            },
            //@ts-ignore
            custom: {},
        },
    });

    const handleOnToggle = (value: boolean, child: string) => {
        const newValue = value ? 'active' : 'inactive';
        setSettingsValue({
            ...settingValue,
            personalized_controls: {
                ...settingValue.personalized_controls,
                [child]: newValue,
            },
        });
        onChange({
            personalized_controls: {
                ...settingValue.personalized_controls,
                [child]: newValue,
            },
        });
    };

    const personalizedControlsData = [
        {
            header: 'HLS Play',
            body: 'Enable HLS video streaming for better playback experience.',
            service_name: 'hls_play',
        },
        {
            header: 'Contest Emails',
            body: 'Receive email notifications about contests and competitions.',
            service_name: 'contest_emails',
        },
        {
            header: 'Advertisements',
            body: 'Allow personalized advertisements on your content.',
            service_name: 'ads',
        },
        {
            header: 'Audition',
            body: 'Enable audition features and notifications.',
            service_name: 'audition',
        },
        {
            header: 'Movie Ticket',
            body: 'Enable movie ticket purchasing and management.',
            service_name: 'movie_ticket',
        },
        {
            header: 'Voting',
            body: 'Enable voting features and participation.',
            service_name: 'voting',
        },
        {
            header: 'Acts',
            body: 'Enable acts and performance features.',
            service_name: 'acts',
        },
    ];

    return (
        <ThemeProvider theme={switchTheme}>
            <Box
                style={styles.flexColumn}
                sx={{
                    gap: '30px',
                    width: '100%',
                    height: '100%',
                    p: {
                        xs: 2,
                        sm: 0,
                    },
                }}>
                <Typography style={styles.title}>Preferences</Typography>
                <Box
                    style={styles.flexColumn}
                    sx={{
                        gap: '40px',
                        width: '100%',
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        borderRadius: 3,
                        p: 3,
                    }}>
                    <Box style={styles.flexColumn} sx={{ gap: '10px', maxWidth: '454px' }}>
                        <Typography style={styles.subtitle}>Personalized Controls</Typography>
                        <Typography style={styles.text}>
                            Customize your experience with personalized controls and preferences.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'flex-start' },
                            justifyContent: { xs: 'center', sm: 'flex-start' },
                            gap: { xs: '20px', sm: '40px' },
                            width: '100%',
                        }}>
                        <FormGroup style={styles.flexColumn} sx={{ gap: '26px' }}>
                            {settingValue &&
                                settingValue.personalized_controls &&
                                personalizedControlsData.map((set, index) => (
                                    <SwitchWithCustomText
                                        body={set.body}
                                        header={set.header}
                                        theme={themeStyle}
                                        switchProps={{
                                            onChange: e => handleOnToggle(e.target.checked, set.service_name),
                                            checked:
                                                settingValue.personalized_controls[
                                                    set.service_name as keyof typeof settingValue.personalized_controls
                                                ] === 'active',
                                        }}
                                        key={index}
                                        {...props}
                                    />
                                ))}
                        </FormGroup>
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
        },
        flexRowBetween: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        linebreak: {
            width: '100%',
            border: `1px solid ${theme[colorScheme].gray_text}`,
        },
        title: {
            fontSize: 36,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            textAlign: 'left',
            color: theme[colorScheme].filters_white,
        },
        subtitle: {
            fontSize: 16,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            textAlign: 'left',
            color: theme[colorScheme].primary_white,
        },
        text: {
            fontSize: 16,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            textAlign: 'left',
            color: theme[colorScheme].tag_white,
        },
    });

export { AccountPreference };
